import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { 
  ArrowLeft, Users, Crown, Play, Copy, Check, 
  UserPlus, Mail, Phone, X, Settings, MessageSquare,
  Wifi, WifiOff, Send, LogOut, Gamepad2, Shield,
  Skull, Trophy, Timer, Swords, Target
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API, WS_URL } from '../lib/backendUrl';
import { getLobbySocket } from '../lib/lobbySocket';

// Reuse guest ID from LobbyBrowser
const getGuestId = () => {
  let guestId = sessionStorage.getItem('metal_lego_guest_id');
  if (!guestId) {
    guestId = 'guest_' + Math.random().toString(36).substr(2, 8);
    sessionStorage.setItem('metal_lego_guest_id', guestId);
  }
  return guestId;
};

// Game mode icons mapping
const MODE_ICONS = {
  coop_campaign: <Gamepad2 className="w-6 h-6" />,
  survival: <Skull className="w-6 h-6" />,
  versus_score: <Trophy className="w-6 h-6" />,
  boss_rush: <Target className="w-6 h-6" />,
  time_attack: <Timer className="w-6 h-6" />,
  last_stand: <Swords className="w-6 h-6" />
};

// Game mode colors
const MODE_COLORS = {
  coop_campaign: { bg: '#14F195', text: '#000' },
  survival: { bg: '#FF3B30', text: '#fff' },
  versus_score: { bg: '#FFD700', text: '#000' },
  boss_rush: { bg: '#9945FF', text: '#fff' },
  time_attack: { bg: '#5AC8FA', text: '#000' },
  last_stand: { bg: '#FF9500', text: '#000' }
};

const SKIN_COLORS = {
  classic: '#FF3B30',
  foot_stabber: '#4A4A4A',
  kill_machine: '#8B0000',
  boss_slayer: '#800080',
  veteran: '#556B2F',
  survivor: '#2F4F4F',
  speed_demon: '#FF4500',
  elite_commander: '#E5E4E2',
  mecha: '#9945FF',
  fire_squad: '#FF9500',
  cyber_ninja: '#14F195',
  golden_warrior: '#FFD700'
};

const MultiplayerLobby = ({ lobbyId, onBack, onStartGame }) => {
  const { publicKey } = useWallet();
  const [lobby, setLobby] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteType, setInviteType] = useState('email');
  const [inviteValue, setInviteValue] = useState('');
  const [copied, setCopied] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showModeSelect, setShowModeSelect] = useState(false);
  const [gameModes, setGameModes] = useState([]);
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const lobbyRef = useRef(null); // Ref to avoid stale closure

  const walletAddress = publicKey?.toBase58() || getGuestId();
  const isHost = lobby?.host_wallet === walletAddress;
  const currentPlayer = lobby?.players?.find(p => p.wallet_address === walletAddress);
  const allReady = lobby?.players?.filter(p => !p.is_host).every(p => p.ready);
  const canStart = isHost && lobby?.players?.length >= 2 && allReady;
  const currentMode = lobby?.game_settings?.game_mode || 'coop_campaign';
  const currentModeData = gameModes.find(m => m.id === currentMode);

  // Keep lobbyRef in sync
  useEffect(() => {
    lobbyRef.current = lobby;
  }, [lobby]);

  // Fetch game modes
  useEffect(() => {
    const fetchModes = async () => {
      try {
        const response = await axios.get(`${API}/game-modes`);
        setGameModes(response.data.modes || []);
      } catch (error) {
        console.error('Error fetching game modes:', error);
      }
    };
    fetchModes();
  }, []);

  // Fetch lobby data
  const fetchLobby = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/lobby/${lobbyId}`);
      setLobby(response.data);
    } catch (error) {
      console.error('Error fetching lobby:', error);
      toast.error('Failed to load lobby');
    } finally {
      setLoading(false);
    }
  }, [lobbyId]);

  // WebSocket connection — shares the lobbySocket singleton with the
  // in-game useMultiplayerSync hook so the screen transition from lobby
  // to game doesn't tear down + reopen the socket.
  useEffect(() => {
    if (!walletAddress || !lobbyId) return;

    const sub = getLobbySocket(lobbyId, walletAddress, WS_URL);
    wsRef.current = sub; // expose `sub` for send-functions below

    const onMessage = (data) => {
      if (data.type === '__socket_open__') {
        setConnected(true);
        return;
      }
      if (data.type === '__socket_close__') {
        setConnected(false);
        return;
      }
      handleWebSocketMessage(data);
    };

    const unsub = sub.subscribe(onMessage);

    return () => {
      unsub();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress, lobbyId]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'lobby_state':
        setLobby(data.lobby);
        break;
      
      case 'player_joined':
        // Dedupe by wallet_address — a slow/duplicate broadcast could
        // otherwise put the same player twice in the ready-up queue.
        setLobby(prev => {
          const existing = prev?.players || [];
          if (existing.some(p => p.wallet_address === data.player.wallet_address)) {
            return prev;
          }
          return { ...prev, players: [...existing, data.player] };
        });
        toast.success(`${data.player.name} joined the lobby`);
        break;
      
      case 'player_left':
        setLobby(prev => ({
          ...prev,
          players: prev?.players?.filter(p => p.wallet_address !== data.wallet_address) || []
        }));
        toast.info('A player left the lobby');
        break;
      
      case 'player_updated':
        setLobby(prev => {
          const players = prev?.players || [];
          const idx = players.findIndex(p => p.wallet_address === data.wallet_address);
          if (idx === -1) {
            // Out-of-order race — update arrived before the corresponding
            // player_joined. Refetch authoritative state instead of dropping.
            fetchLobby();
            return prev;
          }
          const next = [...players];
          next[idx] = { ...next[idx], ready: data.ready, skin_id: data.skin_id };
          return { ...prev, players: next };
        });
        break;
      
      case 'host_assigned':
        // Emitted by the WS handshake to designate the authoritative host.
        // The lobby UI already derives `isHost` from `lobby.host_wallet`,
        // so this is a no-op here — but consuming it explicitly keeps the
        // console clean (no more `Unknown WebSocket message: host_assigned`).
        break;
      
      case 'host_changed':
        setLobby(prev => ({
          ...prev,
          host_wallet: data.new_host,
          players: prev?.players?.map(p => ({
            ...p,
            is_host: p.wallet_address === data.new_host
          })) || []
        }));
        toast.info('Host has changed');
        break;
      
      case 'settings_updated':
        setLobby(prev => ({
          ...prev,
          game_settings: { ...prev?.game_settings, ...data.settings }
        }));
        break;
      
      case 'chat':
        setChatMessages(prev => [...prev, {
          wallet: data.wallet_address,
          message: data.message,
          timestamp: Date.now()
        }]);
        break;
      
      case 'game_starting':
        toast.success('Game starting!');
        setTimeout(() => {
          // Use lobbyRef.current to get latest lobby state (avoids stale closure)
          onStartGame(data.game_session_id, lobbyRef.current);
        }, data.countdown * 1000);
        break;
      
      case 'player_connected':
      case 'player_disconnected':
        fetchLobby();
        break;
      
      default:
        console.log('Unknown WebSocket message:', data);
    }
  }, [fetchLobby, onStartGame]);

  // Initial fetch
  useEffect(() => {
    fetchLobby();
  }, [fetchLobby]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Toggle ready status
  const toggleReady = async () => {
    if (!currentPlayer) return;
    
    try {
      await axios.post(`${API}/lobby/${lobbyId}/update-player`, {
        wallet_address: walletAddress,
        ready: !currentPlayer.ready,
        skin_id: currentPlayer.skin_id
      });
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Auto-start: when all players are ready and there are 2+ players, host auto-starts
  useEffect(() => {
    if (!lobby || !isHost) return;
    const players = lobby.players || [];
    if (players.length < 2) return;
    const nonHostPlayers = players.filter(p => !p.is_host);
    const allNonHostReady = nonHostPlayers.length > 0 && nonHostPlayers.every(p => p.ready);
    if (allNonHostReady) {
      // Auto-start the game
      axios.post(`${API}/lobby/${lobbyId}/start?host_wallet=${walletAddress}`).catch(() => {});
    }
  }, [lobby, isHost, lobbyId, walletAddress]);

  // Start game (manual, kept for host button)
  const handleStartGame = async () => {
    if (!canStart) return;
    
    try {
      await axios.post(`${API}/lobby/${lobbyId}/start?host_wallet=${walletAddress}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start game');
    }
  };

  // Leave lobby
  const handleLeave = async () => {
    try {
      await axios.post(`${API}/lobby/${lobbyId}/leave?wallet_address=${walletAddress}`);
      onBack();
    } catch (error) {
      toast.error('Failed to leave lobby');
    }
  };

  // Send invite
  const handleSendInvite = async () => {
    if (!inviteValue.trim()) return;
    
    try {
      const response = await axios.post(`${API}/lobby/invite`, {
        lobby_id: lobbyId,
        host_wallet: walletAddress,
        invite_type: inviteType,
        invite_value: inviteValue
      });
      
      toast.success(response.data.message);
      setInviteValue('');
      setShowInvite(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  // Copy invite link
  const copyInviteLink = () => {
    const link = `${window.location.origin}/lobby/${lobbyId}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Invite link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Send chat message
  const sendChatMessage = () => {
    if (!chatInput.trim() || !wsRef.current) return;
    // wsRef.current is now a lobbySocket subscriber handle (object with a
    // .send(obj) method) — not a raw WebSocket. It auto-serialises to JSON.
    wsRef.current.send({
      type: 'chat',
      message: chatInput.trim()
    });
    setChatInput('');
  };

  // Update settings
  const updateSetting = async (key, value) => {
    if (!isHost) return;
    
    try {
      await axios.post(`${API}/lobby/${lobbyId}/settings?host_wallet=${walletAddress}`, {
        [key]: value
      });
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Change game mode
  const changeGameMode = async (modeId) => {
    if (!isHost) return;
    
    try {
      await axios.post(`${API}/lobby/${lobbyId}/settings?host_wallet=${walletAddress}`, {
        game_mode: modeId
      });
      setShowModeSelect(false);
      toast.success(`Game mode changed to ${gameModes.find(m => m.id === modeId)?.name || modeId}`);
    } catch (error) {
      toast.error('Failed to change game mode');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#14F195] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-[#A1A1AA] font-game">Loading lobby...</p>
        </div>
      </div>
    );
  }

  if (!lobby) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#FF4444] font-game text-xl mb-4">Lobby not found</p>
          <Button onClick={onBack} className="game-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={handleLeave}
            variant="ghost"
            className="text-[#FF4444] hover:text-[#FF4444]/80 hover:bg-[#FF4444]/10"
            data-testid="leave-lobby-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Leave Lobby
          </Button>
          
          <div className="flex items-center gap-2">
            {connected ? (
              <span className="flex items-center gap-1 text-[#14F195] text-sm">
                <Wifi className="w-4 h-4" />
                Connected
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[#FF4444] text-sm">
                <WifiOff className="w-4 h-4" />
                Reconnecting...
              </span>
            )}
          </div>
        </div>

        {/* Lobby Info Card */}
        <div className="glass-card p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-game text-2xl md:text-3xl text-[#14F195] mb-2">
                {lobby.lobby_name}
              </h1>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-[#A1A1AA]">
                  Lobby Code: <span className="text-[#FFD700] font-game" data-testid="lobby-code">{lobby.lobby_id}</span>
                </span>
                <span className="text-[#A1A1AA]">
                  <Users className="w-4 h-4 inline mr-1" />
                  {lobby.players?.length || 0}/{lobby.max_players}
                </span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={copyInviteLink}
                className="bg-[#333] hover:bg-[#444] text-white"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
              
              {isHost && (
                <>
                  <Button
                    onClick={() => setShowInvite(true)}
                    className="bg-[#9945FF] hover:bg-[#9945FF]/90"
                    data-testid="invite-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    className="bg-[#333] hover:bg-[#444]"
                    data-testid="settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Players Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {lobby.players?.map((player, index) => (
            <div
              key={player.wallet_address}
              className={`glass-card p-4 border-2 transition-all ${
                player.ready || player.is_host 
                  ? 'border-[#14F195]' 
                  : 'border-[#333]'
              }`}
              data-testid={`player-card-${index}`}
            >
              <div className="flex items-center gap-4">
                {/* Player Avatar */}
                <div 
                  className="w-16 h-16 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: SKIN_COLORS[player.skin_id] || '#FF3B30' }}
                >
                  <Gamepad2 className="w-8 h-8 text-white" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-game text-white">
                      {player.name}
                    </span>
                    {player.is_host && (
                      <Crown className="w-4 h-4 text-[#FFD700]" title="Host" />
                    )}
                    {player.wallet_address === walletAddress && (
                      <span className="text-xs text-[#14F195]">(You)</span>
                    )}
                  </div>
                  <div className="text-xs text-[#666] truncate max-w-[200px]">
                    {player.wallet_address.slice(0, 4)}...{player.wallet_address.slice(-4)}
                  </div>
                  <div className="mt-2">
                    {player.is_host ? (
                      <span className="text-xs bg-[#FFD700]/20 text-[#FFD700] px-2 py-1 rounded font-game">
                        HOST
                      </span>
                    ) : player.ready ? (
                      <span className="text-xs bg-[#14F195]/20 text-[#14F195] px-2 py-1 rounded font-game">
                        READY
                      </span>
                    ) : (
                      <span className="text-xs bg-[#333] text-[#A1A1AA] px-2 py-1 rounded font-game">
                        NOT READY
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Empty slots */}
          {Array.from({ length: lobby.max_players - (lobby.players?.length || 0) }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="glass-card p-4 border-2 border-dashed border-[#333] flex items-center justify-center h-[104px]"
            >
              <span className="text-[#666] font-game text-sm">Waiting for player...</span>
            </div>
          ))}
        </div>

        {/* Game Mode Display */}
        <div 
          className={`glass-card p-4 mb-4 cursor-pointer transition-all hover:border-opacity-50 ${isHost ? 'hover:scale-[1.01]' : ''}`}
          style={{ 
            borderColor: MODE_COLORS[currentMode]?.bg || '#14F195',
            borderWidth: '2px'
          }}
          onClick={() => isHost && setShowModeSelect(true)}
          data-testid="game-mode-display"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: MODE_COLORS[currentMode]?.bg || '#14F195' }}
              >
                <span style={{ color: MODE_COLORS[currentMode]?.text || '#000' }}>
                  {MODE_ICONS[currentMode] || <Gamepad2 className="w-6 h-6" />}
                </span>
              </div>
              <div>
                <div className="font-game text-lg" style={{ color: MODE_COLORS[currentMode]?.bg }}>
                  {currentModeData?.name || 'Co-op Campaign'}
                </div>
                <div className="text-[#A1A1AA] text-sm">
                  {currentModeData?.description || 'Team up and progress through story levels together'}
                </div>
              </div>
            </div>
            {isHost && (
              <div className="text-[#666] text-sm flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Click to change
              </div>
            )}
          </div>
          
          {/* Mode Features */}
          {currentModeData?.features && (
            <div className="flex flex-wrap gap-2 mt-3">
              {currentModeData.features.map((feature, i) => (
                <span 
                  key={i}
                  className="text-xs px-2 py-1 rounded-full bg-[#1a1a1a] text-[#A1A1AA]"
                >
                  {feature.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Game Settings Display */}
        <div className="glass-card p-4 mb-6">
          <h3 className="font-game text-sm text-[#A1A1AA] mb-3">GAME SETTINGS</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <div className="text-[#666]">Difficulty</div>
              <div className="text-[#14F195] font-game capitalize">{lobby.game_settings?.difficulty}</div>
            </div>
            <div className="text-center">
              <div className="text-[#666]">Friendly Fire</div>
              <div className={`font-game ${lobby.game_settings?.friendly_fire ? 'text-[#FF4444]' : 'text-[#14F195]'}`}>
                {lobby.game_settings?.friendly_fire ? 'ON' : 'OFF'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-[#666]">Shared Lives</div>
              <div className={`font-game ${lobby.game_settings?.shared_lives ? 'text-[#14F195]' : 'text-[#FF4444]'}`}>
                {lobby.game_settings?.shared_lives ? 'ON' : 'OFF'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-[#666]">Starting Level</div>
              <div className="text-[#FFD700] font-game">{lobby.game_settings?.starting_level}</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          {isHost ? (
            <Button
              onClick={handleStartGame}
              disabled={!canStart}
              className={`flex-1 py-6 text-lg font-game ${
                canStart 
                  ? 'bg-[#14F195] text-black hover:bg-[#14F195]/90' 
                  : 'bg-[#333] text-[#666] cursor-not-allowed'
              }`}
              data-testid="start-game-btn"
            >
              <Play className="w-6 h-6 mr-3" />
              {canStart ? 'START GAME' : 
               lobby.players?.length < 2 ? 'NEED MORE PLAYERS' : 
               'WAITING FOR PLAYERS TO READY'}
            </Button>
          ) : (
            <Button
              onClick={toggleReady}
              className={`flex-1 py-6 text-lg font-game ${
                currentPlayer?.ready 
                  ? 'bg-[#FF4444] hover:bg-[#FF4444]/90' 
                  : 'bg-[#14F195] text-black hover:bg-[#14F195]/90'
              }`}
              data-testid="ready-btn"
            >
              <Shield className="w-6 h-6 mr-3" />
              {currentPlayer?.ready ? 'CANCEL READY' : 'READY!'}
            </Button>
          )}
          
          <Button
            onClick={() => setShowChat(!showChat)}
            className="bg-[#333] hover:bg-[#444] py-6"
            data-testid="chat-toggle-btn"
          >
            <MessageSquare className="w-6 h-6" />
            {chatMessages.length > 0 && (
              <span className="ml-2 bg-[#FF4444] text-white text-xs px-2 py-0.5 rounded-full">
                {chatMessages.length}
              </span>
            )}
          </Button>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="glass-card mt-4 p-4">
            <h3 className="font-game text-sm text-[#A1A1AA] mb-3">LOBBY CHAT</h3>
            <div className="h-48 overflow-y-auto mb-3 bg-[#0a0a0a] rounded-lg p-3">
              {chatMessages.length === 0 ? (
                <p className="text-[#666] text-sm text-center mt-16">No messages yet</p>
              ) : (
                chatMessages.map((msg, i) => {
                  const sender = lobby.players?.find(p => p.wallet_address === msg.wallet);
                  return (
                    <div key={i} className="mb-2">
                      <span className="text-[#9945FF] font-game text-xs">
                        {sender?.name || 'Unknown'}:
                      </span>
                      <span className="text-white text-sm ml-2">{msg.message}</span>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type a message..."
                className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#14F195]"
                data-testid="chat-input"
              />
              <Button onClick={sendChatMessage} className="bg-[#14F195] text-black">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        {showInvite && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 max-w-md w-full" data-testid="invite-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-game text-xl text-[#14F195]">INVITE PLAYER</h2>
                <Button 
                  onClick={() => setShowInvite(false)} 
                  variant="ghost" 
                  className="text-[#A1A1AA]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Invite Type Toggle */}
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={() => setInviteType('email')}
                  className={`flex-1 ${inviteType === 'email' ? 'bg-[#14F195] text-black' : 'bg-[#333]'}`}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button
                  onClick={() => setInviteType('phone')}
                  className={`flex-1 ${inviteType === 'phone' ? 'bg-[#14F195] text-black' : 'bg-[#333]'}`}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Phone
                </Button>
              </div>
              
              {/* Input */}
              <input
                type={inviteType === 'email' ? 'email' : 'tel'}
                value={inviteValue}
                onChange={(e) => setInviteValue(e.target.value)}
                placeholder={inviteType === 'email' ? 'Enter email address' : 'Enter phone number'}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white mb-4 focus:outline-none focus:border-[#14F195]"
                data-testid="invite-input"
              />
              
              {/* Share Link */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 mb-4">
                <p className="text-[#A1A1AA] text-xs mb-2">Or share this link:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[#14F195] text-sm truncate">
                    {window.location.origin}/lobby/{lobby.lobby_id}
                  </code>
                  <Button onClick={copyInviteLink} size="sm" className="bg-[#333]">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <Button
                onClick={handleSendInvite}
                disabled={!inviteValue.trim()}
                className="w-full bg-[#9945FF] hover:bg-[#9945FF]/90 py-3"
                data-testid="send-invite-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                SEND INVITE
              </Button>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && isHost && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 max-w-md w-full" data-testid="settings-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-game text-xl text-[#14F195]">GAME SETTINGS</h2>
                <Button 
                  onClick={() => setShowSettings(false)} 
                  variant="ghost" 
                  className="text-[#A1A1AA]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Difficulty */}
              <div className="mb-4">
                <label className="block text-[#A1A1AA] text-sm mb-2">Difficulty</label>
                <div className="flex gap-2">
                  {['easy', 'normal', 'hard'].map(diff => (
                    <Button
                      key={diff}
                      onClick={() => updateSetting('difficulty', diff)}
                      className={`flex-1 capitalize ${
                        lobby.game_settings?.difficulty === diff 
                          ? 'bg-[#14F195] text-black' 
                          : 'bg-[#333]'
                      }`}
                    >
                      {diff}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Starting Level */}
              <div className="mb-4">
                <label className="block text-[#A1A1AA] text-sm mb-2">Starting Level</label>
                <div className="flex gap-2">
                  {[1, 5, 10].map(lvl => (
                    <Button
                      key={lvl}
                      onClick={() => updateSetting('starting_level', lvl)}
                      className={`flex-1 ${
                        lobby.game_settings?.starting_level === lvl 
                          ? 'bg-[#14F195] text-black' 
                          : 'bg-[#333]'
                      }`}
                    >
                      Level {lvl}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Toggles */}
              <div className="space-y-3">
                <div 
                  className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg cursor-pointer"
                  onClick={() => updateSetting('friendly_fire', !lobby.game_settings?.friendly_fire)}
                >
                  <span className="text-white">Friendly Fire</span>
                  <div className={`w-12 h-6 rounded-full transition-colors ${
                    lobby.game_settings?.friendly_fire ? 'bg-[#FF4444]' : 'bg-[#333]'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full mt-0.5 transition-transform ${
                      lobby.game_settings?.friendly_fire ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </div>
                </div>
                
                <div 
                  className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg cursor-pointer"
                  onClick={() => updateSetting('shared_lives', !lobby.game_settings?.shared_lives)}
                >
                  <span className="text-white">Shared Lives</span>
                  <div className={`w-12 h-6 rounded-full transition-colors ${
                    lobby.game_settings?.shared_lives ? 'bg-[#14F195]' : 'bg-[#333]'
                  }`}>
                    <div className={`w-5 h-5 bg-white rounded-full mt-0.5 transition-transform ${
                      lobby.game_settings?.shared_lives ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Game Mode Selection Modal */}
        {showModeSelect && isHost && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="glass-card p-6 max-w-2xl w-full my-8" data-testid="mode-select-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-game text-xl text-[#14F195]">SELECT GAME MODE</h2>
                <Button 
                  onClick={() => setShowModeSelect(false)} 
                  variant="ghost" 
                  className="text-[#A1A1AA]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gameModes.map((mode) => {
                  const isSelected = currentMode === mode.id;
                  const modeColor = MODE_COLORS[mode.id] || { bg: '#14F195', text: '#000' };
                  
                  return (
                    <div
                      key={mode.id}
                      onClick={() => changeGameMode(mode.id)}
                      className={`p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.02] ${
                        isSelected ? 'ring-2' : 'hover:bg-[#1a1a1a]'
                      }`}
                      style={{
                        backgroundColor: isSelected ? `${modeColor.bg}20` : 'transparent',
                        borderColor: modeColor.bg,
                        borderWidth: isSelected ? '2px' : '1px',
                        borderStyle: 'solid',
                        ringColor: modeColor.bg
                      }}
                      data-testid={`mode-${mode.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: modeColor.bg }}
                        >
                          <span style={{ color: modeColor.text }}>
                            {MODE_ICONS[mode.id] || <Gamepad2 className="w-5 h-5" />}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span 
                              className="font-game text-sm"
                              style={{ color: modeColor.bg }}
                            >
                              {mode.name}
                            </span>
                            {isSelected && (
                              <Check className="w-4 h-4" style={{ color: modeColor.bg }} />
                            )}
                          </div>
                          <p className="text-[#A1A1AA] text-xs mt-1">{mode.description}</p>
                          
                          {/* Mode features */}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {mode.features?.slice(0, 3).map((feature, i) => (
                              <span 
                                key={i}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-[#333] text-[#666]"
                              >
                                {feature.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                          
                          {/* Player count */}
                          <div className="mt-2 text-[10px] text-[#666]">
                            {mode.min_players}-{mode.max_players} players
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Mode-specific tips */}
              <div className="mt-6 p-3 bg-[#1a1a1a] rounded-lg">
                <h4 className="font-game text-xs text-[#FFD700] mb-2">
                  {currentModeData?.name || 'Co-op Campaign'} - TIPS
                </h4>
                <p className="text-[#A1A1AA] text-xs">
                  {currentMode === 'coop_campaign' && "Work together to progress through story levels. Revive fallen teammates!"}
                  {currentMode === 'survival' && "Enemies get stronger each wave. Coordinate power-up pickups!"}
                  {currentMode === 'versus_score' && "Every kill counts! Compete for the highest individual score."}
                  {currentMode === 'boss_rush' && "Face boss after boss. Focus fire and dodge together!"}
                  {currentMode === 'time_attack' && "Speed is key! Kills add time, work fast as a team."}
                  {currentMode === 'last_stand' && "One life each. Be careful - friendly fire is ON!"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiplayerLobby;
